import React, { useEffect, useState } from "react";
import classNames from "classnames";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import { isEmpty } from "lodash";

import getPDFTranslations from "utils/translation/f280-pdf";
import { useStateSelector } from "utils/field";
import Drawer from "components/Drawer";
import Button from "components/Button";
import Input from "components/Input";
import Checkbox from "components/Input/Checkbox";
import LangButton from "components/LangButton";
import F280PDF from "./f280Pdf";

import Export from "assets/images/icons/JSX/Export";
import classes from "./styles.module.scss";
import mixpanel from "mixpanel-browser";

const ExportF280PDF = ({
    isPdfExportOpen,
    handleClose,
    displayData,
    name,
    calcPerformedBy = {},
    heatLossValue,
    latHeatGainValue,
    latentMultiplier,
    heatUnit,
    roomByRoomResults = {},
    storeyCategories,
    allowedRoomByRoom = false,
}) => {
    const [exporting, toggleExporting] = useState(false);
    const [lang, setLang] = useState("en");
    const verifiedF280User = useStateSelector("user.verifiedF280User") ?? false;

    const [inputFieldsData, setInputFieldsData] = useState({
        issuedForTheUseOf: "",
        projectNo: "",
        lot: "",
        site: "",
        notes: "",
        additionalDocsList: "",
        email: calcPerformedBy?.email || "",
        dimInfoBasedOn: "",
        designSummary: false,
        roomByRoomResults: !isEmpty(roomByRoomResults),
    });

    useEffect(() => {
        if (!isEmpty(roomByRoomResults)) {
            handleChange(true, "roomByRoomResults");
        }
    }, [roomByRoomResults]);

    const handleChange = (value, name) => setInputFieldsData({ ...inputFieldsData, [name]: value });

    const { t } = getPDFTranslations(lang);

    // Generate the report as a real vector PDF (US Letter) via @react-pdf/renderer.
    // No DOM/canvas rasterization — this replaces the html2canvas path that could
    // intermittently produce a black screen and oversized, non-standard pages.
    const handleExportPdf = async () => {
        toggleExporting(true);
        try {
            const blob = await pdf(
                <F280PDF
                    displayData={displayData}
                    inputFieldsData={inputFieldsData}
                    calcPerformedBy={calcPerformedBy}
                    name={name}
                    heatLossValue={heatLossValue}
                    latHeatGainValue={latHeatGainValue}
                    latentMultiplier={latentMultiplier}
                    heatUnit={heatUnit}
                    language={lang}
                    includeRoomByRoomResults={allowedRoomByRoom ? inputFieldsData.roomByRoomResults : false}
                    roomByRoomResults={roomByRoomResults}
                    storeyCategories={storeyCategories}
                />
            ).toBlob();

            saveAs(blob, `${name}_f280_report_${lang}.pdf`);

            mixpanel.track("F280 PDF Export", {
                ExportType: inputFieldsData.roomByRoomResults ? "Room-by-room" : "Whole-home",
            });
        } catch (error) {
            console.error("Error generating PDF:", error);
        } finally {
            toggleExporting(false);
        }
    };

    return (
        <Drawer
            open={isPdfExportOpen}
            close={handleClose}
            title={t("f280pdf.exportF280", "Export F280 Report [!!]")}
            subtitle={t(
                "f280pdf.drawerInstructions",
                "Please provide the following information for your F280 report, and make sure to sign or stamp the bottom right area of the PDF. [!!]"
            )}
        >
            <div
                className={classNames(classes.inputRow, classes.firstInputRow)}
            >
                <Input
                    name={"email"}
                    label={t("f280pdf.email", "E-mail [!!]")}
                    type={"text"}
                    input={{
                        value: inputFieldsData.email,
                        onChange: (value, name) => handleChange(value, name),
                    }}
                    className={classes.emailInput}
                />
                <LangButton lang={lang} setLang={setLang} />
            </div>
            <div className={classes.inputFields}>
                <div
                    className={classes.inputRow}
                >
                    <Input
                        name={"issuedForTheUseOf"}
                        label={t("f280pdf.issuedForTheUseOf", "Documents issued for the use of: [!!]")}
                        type={"text"}
                        input={{
                            value: inputFieldsData.issuedForTheUseOf,
                            onChange: (value, name) => handleChange(value, name),
                        }}
                        style={{ flex: 1 }}
                    />
                    <Input
                        name={"projectNo"}
                        label={t("f280pdf.projectNo", "Project number [!!]")}
                        type={"text"}
                        input={{
                            value: inputFieldsData.projectNo,
                            onChange: (value, name) => handleChange(value, name),
                        }}
                        style={{ flex: 1 }}
                    />
                </div>
                <div
                    className={classes.inputRow}
                >
                    <Input
                        name={"site"}
                        label={t("f280pdf.buildingLocSite", "Building location - Site [!!]")}
                        type={"text"}
                        input={{
                            value: inputFieldsData.site,
                            onChange: (value, name) => handleChange(value, name),
                        }}
                        style={{ flex: 1 }}
                    />
                    <Input
                        name={"lot"}
                        label={t("f280pdf.buildingLocLot", "Building location - Lot [!!]")}
                        type={"text"}
                        input={{
                            value: inputFieldsData.lot,
                            onChange: (value, name) => handleChange(value, name),
                        }}
                        style={{ flex: 1 }}
                    />
                </div>

                <Input
                    name={"dimInfoBasedOn"}
                    label={t("f280pdf.infoBasedOn", "Dimensional information based on: [!!]")}
                    type={"text"}
                    input={{
                        value: inputFieldsData.dimInfoBasedOn,
                        onChange: (value, name) => handleChange(value, name),
                    }}
                />
                <Input
                    name={"notes"}
                    label={t("f280pdf.notes", "Notes [!!]")}
                    type={"text"}
                    input={{
                        value: inputFieldsData.notes,
                        onChange: (value, name) => handleChange(value, name),
                    }}
                />
                <div
                    className={classes.inputRow}
                >
                    <Input
                        name={"additionalDocsList"}
                        label={t("f280pdf.additionalDocsList", "List any additional documents [!!]")}
                        type={"text"}
                        input={{
                            value: inputFieldsData.additionalDocsList,
                            onChange: (value, name) => handleChange(value, name),
                        }}
                        style={{ flex: 1 }}
                    />
                </div>
                <div
                    className={classes.inputRow}
                >
                    <Checkbox
                        style={{ marginTop: "20px", flex: 1 }}
                        input={{
                            value: inputFieldsData.designSummary,
                            onChange: (value) => handleChange(value, "designSummary"),
                        }}
                        label={t("f280pdf.designSummary", "Design Summary [!!]")}
                    />
                    <Checkbox
                        style={{ marginTop: "20px", flex: 1 }}
                        input={{
                            value: inputFieldsData.roomByRoomResults,
                            onChange: (value) => handleChange(value, "roomByRoomResults"),
                        }}
                        label={t("f280pdf.roomByRoom", "Room by Room [!!]")}
                    />
                </div>
            </div>

            {!verifiedF280User ? (
                <div className={classes.unverifiedDesignerWarning}>
                    <div className={classes.designerWarningText}>
                        <p>
                            {t(
                                "f280pdf.warningVerificationStatus",
                                "Qualified CSA F280-12 designer verification status:"
                            )}{" "}
                            <strong>{t("f280pdf.statusFalse", "False")}</strong>
                        </p>
                        <p>
                            {t(
                                "f280pdf.warningUnverifiedContact",
                                "It is highly recommended that you complete an F280 Heat Loss / Heat Gain training course before providing this service. If you have completed an F280 training course and are ready to be verified, please contact info@voltaresearch.org."
                            )}
                        </p>
                    </div>
                </div>
            ) : (
                <div className={classes.verifiedDesignerWarning}>
                    <div className={classes.designerWarningText}>
                        <p>
                            {t(
                                "f280pdf.warningVerificationStatus",
                                "Qualified CSA F280-12 designer verification status:"
                            )}{" "}
                            <strong>{t("f280pdf.statusTrue", "True")}</strong>
                        </p>
                    </div>
                </div>
            )}
            <div className={classes.pdfFormattingWarning}>
                <div className={classes.designerWarningText}>
                    <p>
                        {t(
                            "f280pdf.warningPdfFormatting",
                            "If your PDF is not formatting properly, try restarting your browser."
                        )}
                    </p>
                </div>
            </div>

            <div className={classes.pdfBtnsContainer}>
                <Button type="hollow" onClick={handleClose} style={{ width: "144px" }}>
                    {t("f280pdf.cancel", "Cancel [!!]")}
                </Button>
                <Button
                    // smallPadding
                    icon={Export}
                    onClick={handleExportPdf}
                    disabled={exporting}
                    className={classes.exportButton}
                >
                    {exporting
                        ? t("f280pdf.exporting", "Exporting... [!!]")
                        : t("f280pdf.exportPDF", "Export PDF [!!]")}
                </Button>
            </div>
        </Drawer>
    );
};

export default ExportF280PDF;
